<script setup>
import { useTheme } from 'vuetify'
import { useRouter, useRoute } from 'vue-router'
import { onMounted, computed, defineAsyncComponent } from 'vue'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import {
  initConfigStore,
  useConfigStore,
} from '@core/stores/config'
import { useTenantAuthStore } from '@/stores/tenantAuth.js'
import { useSaAuthStore } from '@/stores/saAuth.js'
import { hexToRgb } from '@core/utils/colorConverter'

// Dynamically import layouts
const DefaultLayout = defineAsyncComponent(() => import('@/layouts/default.vue'))
const BlankLayout = defineAsyncComponent(() => import('@/layouts/blank.vue'))

const { global } = useTheme()
const router = useRouter()
const route = useRoute()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

const configStore = useConfigStore()

// Determine which layout to use based on route metadata
const currentLayout = computed(() => {
  const layoutName = route.meta?.layout || 'default'
  
  if (layoutName === 'blank')
    return BlankLayout
  
  return DefaultLayout
})

onMounted(() => {
  // Handle 401 unauthorized errors from API
  window.addEventListener('auth:unauthorized', (event) => {
    const tenantAuth = useTenantAuthStore()
    const saAuth = useSaAuthStore()
    
    // Logout and redirect to appropriate login page
    if (router.currentRoute.value.path.startsWith('/tenant')) {
      tenantAuth.user = null
      router.push('/tenant/login')
    } else if (router.currentRoute.value.path.startsWith('/superadmin')) {
      saAuth.user = null
      router.push('/superadmin/login')
    }
  })
})
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <!-- Dynamically apply layout based on route metadata -->
      <Component :is="currentLayout">
        <RouterView />
      </Component>

      <ScrollToTop />
    </VApp>
  </VLocaleProvider>
</template>
